<template>
  <div class="profile-detail-container">
    <!-- 操作按钮区（打印时自动隐藏） -->
    <div class="action-bar no-print">
      <el-button
        type="primary"
        icon="el-icon-printer"
        size="small"
        @click="handlePrint"
      >
        打印 / 导出 PDF (A4 优化版)
      </el-button>
    </div>

    <!-- A4 纸张主体内容区 -->
    <div id="printArea" class="a4-page">
      <!-- 档案页眉 -->
      <div class="a4-header">
        <h2>{{ form.name }} 的个人档案</h2>
        <p class="doc-subtitle">机密档案文件 · 仅供内部阅读</p>
      </div>

      <!-- 一、 基本信息 -->
      <div class="section-block">
        <h4 class="section-title"><i class="el-icon-s-custom" /> 基本信息</h4>
        <el-descriptions :column="3" border size="small" class="custom-descriptions">
          <el-descriptions-item label="姓名">{{ form.name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="性别">{{ form.gender || '-' }}</el-descriptions-item>
          <el-descriptions-item label="出生日期">{{ form.birthDate || '-' }}</el-descriptions-item>
          <el-descriptions-item label="籍贯">{{ formatRegion(form.nativePlace) }}</el-descriptions-item>
          <el-descriptions-item label="民族">{{ form.ethnicity || '-' }}</el-descriptions-item>
          <el-descriptions-item label="政治面貌">{{ form.politicalStatus || '-' }}</el-descriptions-item>
          <el-descriptions-item label="身份证号" :span="2">{{ form.idCard || '-' }}</el-descriptions-item>
          <el-descriptions-item label="手机号">{{ form.phone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="学历" :span="3">{{ form.education || '-' }}</el-descriptions-item>
          <el-descriptions-item label="户籍地址" :span="3">
            {{ formatRegion(form.householdRegion) }} {{ form.householdDetail || '' }}
          </el-descriptions-item>
          <el-descriptions-item label="居住地址" :span="3">
            {{ formatRegion(form.livingRegion) }} {{ form.livingDetail || '' }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 二、 家庭成员 -->
      <div class="section-block">
        <h4 class="section-title"><i class="el-icon-s-home" /> 家庭成员</h4>
        <el-table v-if="form.familyMembers && form.familyMembers.length > 0" :data="form.familyMembers" border size="mini" class="print-table">
          <el-table-column type="index" label="序号" width="45" align="center" />
          <el-table-column prop="relation" label="关系" width="75" />
          <el-table-column prop="name" label="姓名" width="90" />
          <el-table-column prop="idCard" label="身份证号" min-width="150" />
          <el-table-column prop="phone" label="手机号" width="105" />
        </el-table>
        <p v-else class="empty-text">暂无家庭成员记录</p>
      </div>

      <!-- 三、 教育经历 -->
      <div class="section-block">
        <h4 class="section-title"><i class="el-icon-reading" /> 教育经历</h4>
        <el-table v-if="form.educationHistory && form.educationHistory.length > 0" :data="form.educationHistory" border size="mini" class="print-table">
          <el-table-column type="index" label="序号" width="45" align="center" />
          <el-table-column prop="schoolName" label="学校名称" width="120" />
          <el-table-column label="学校地址" min-width="160">
            <template slot-scope="scope">
              {{ formatRegion(scope.row.schoolRegion) }} {{ scope.row.schoolDetail || '' }}
            </template>
          </el-table-column>
          <el-table-column label="起止时间" width="165" align="center">
            <template slot-scope="scope">
              {{ scope.row.timeRange ? scope.row.timeRange.join(' 至 ') : '-' }}
            </template>
          </el-table-column>
        </el-table>
        <p v-else class="empty-text">暂无教育经历记录</p>
      </div>

      <!-- 四、 工作经历 -->
      <div class="section-block">
        <h4 class="section-title"><i class="el-icon-s-cooperation" /> 工作经历</h4>
        <el-table v-if="form.workHistory && form.workHistory.length > 0" :data="form.workHistory" border size="mini" class="print-table">
          <el-table-column type="index" label="序号" width="45" align="center" />
          <el-table-column prop="companyName" label="公司名称" width="110" />
          <el-table-column prop="position" label="职位" width="85" />
          <el-table-column label="公司地址" min-width="140">
            <template slot-scope="scope">
              {{ formatRegion(scope.row.companyRegion) }} {{ scope.row.companyDetail || '' }}
            </template>
          </el-table-column>
          <el-table-column label="起止时间" width="165" align="center">
            <template slot-scope="scope">
              {{ scope.row.timeRange ? scope.row.timeRange.join(' 至 ') : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="income" label="年收入" width="75" align="center" />
        </el-table>
        <p v-else class="empty-text">暂无工作经历记录</p>
      </div>

      <!-- 五、 感情经历 -->
      <div class="section-block">
        <h4 class="section-title"><i class="el-icon-star-on" /> 感情经历</h4>
        <el-table v-if="form.relationshipHistory && form.relationshipHistory.length > 0" :data="form.relationshipHistory" border size="mini" class="print-table">
          <el-table-column type="index" label="序号" width="45" align="center" />
          <el-table-column prop="partner" label="对象" width="75" />
          <el-table-column prop="idCard" label="对象身份证号" min-width="140" />
          <el-table-column prop="phone" label="手机号" width="100" />
          <el-table-column prop="intimacyLevel" label="交往程度" width="80" align="center" />
          <el-table-column prop="relationshipStatus" label="发展阶段" width="80" align="center" />
          <el-table-column label="起止时间" width="155" align="center">
            <template slot-scope="scope">
              {{ scope.row.timeRange ? scope.row.timeRange.join(' 至 ') : '-' }}
            </template>
          </el-table-column>
        </el-table>
        <p v-else class="empty-text">暂无感情经历记录</p>
      </div>

      <!-- A4 底部页脚标识 -->
      <div class="a4-footer">
        <span>生成时间：{{ currentDate }}</span>
        <span>档案管理系统生成</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getPersonalData } from '@/api/user'

export default {
  name: 'UserProfileDetailA4',
  data() {
    return {
      currentDate: new Date().toLocaleDateString(),
      form: {
        name: '',
        gender: '',
        birthDate: '',
        nativePlace: [],
        ethnicity: '',
        politicalStatus: '',
        idCard: '',
        phone: '',
        education: '',
        householdRegion: [],
        householdDetail: '',
        livingRegion: [],
        livingDetail: '',
        familyMembers: [],
        educationHistory: [],
        workHistory: [],
        relationshipHistory: []
      }
    }
  },
  created() {
    this.getData()
  },
  methods: {
    getData() {
      getPersonalData().then(resp => {
        if (resp.code === 0) {
          this.form = resp.data
        } else {
          this.$message.warning(resp.msg || '获取档案数据失败')
        }
      }).catch(err => {
        console.error(err)
        this.$message.error('网络异常，获取档案数据失败')
      })
    },
    formatRegion(val) {
      if (!val || !Array.isArray(val) || val.length === 0) return '-'
      return val.join(' / ')
    },
    handlePrint() {
      window.print()
    }
  }
}
</script>

<style scoped>
/* 外层容器居中 */
.profile-detail-container {
  background-color: #e2e8f0;
  padding: 20px 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 操作栏 */
.action-bar {
  width: 210mm;
  margin-bottom: 12px;
  display: flex;
  justify-content: flex-end;
}

/* 模拟标准 A4 纸张 (210mm x 297mm) */
.a4-page {
  width: 210mm;
  min-height: 297mm;
  padding: 12mm 15mm;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  position: relative;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", Arial, sans-serif;
  color: #333;
  overflow: hidden; /* 防止内部元素意外撑破 A4 边界 */
}

.a4-header {
  text-align: center;
  border-bottom: 2px solid #2b569a;
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.a4-header h2 {
  margin: 0;
  font-size: 18px;
  color: #1a365d;
  letter-spacing: 1px;
}

.doc-subtitle {
  margin: 3px 0 0 0;
  font-size: 10px;
  color: #718096;
}

.section-block {
  margin-bottom: 12px;
  page-break-inside: avoid;
}

.section-title {
  font-size: 12px;
  font-weight: bold;
  color: #2b569a;
  border-left: 3px solid #2b569a;
  padding-left: 6px;
  margin: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-text {
  font-size: 11px;
  color: #a0aec0;
  text-align: center;
  padding: 6px 0;
  background: #f7fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 4px;
  margin: 0;
}

.a4-footer {
  margin-top: 20px;
  border-top: 1px solid #e2e8f0;
  padding-top: 6px;
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  color: #a0aec0;
}

/* ==================== 针对 A4 表格宽度的精准防溢出修复 ==================== */
.print-table {
  width: 100% !important;
  max-width: 100% !important;
  font-size: 11px !important;
}

/* 强制 el-table 内部表格元素宽度百分百贴合，绝不向右撑大 */
::v-deep .print-table .el-table__header-wrapper table,
::v-deep .print-table .el-table__body-wrapper table {
  width: 100% !important;
}

/* 缩减 Element UI 表格单元格内边距 */
::v-deep .print-table th,
::v-deep .print-table td {
  padding: 3px 0 !important;
  height: 26px !important;
}

::v-deep .print-table .cell {
  padding-left: 4px !important;
  padding-right: 4px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

/* 清除滚动条 Gutter 占位空白 */
::v-deep .el-table th.gutter {
  display: none !important;
}

::v-deep .el-table colgroup col[name="gutter"] {
  width: 0 !important;
}

/* ==================== 打印专用媒体查询 (@media print) ==================== */
@media print {
  body, .profile-detail-container {
    background: none !important;
    padding: 0 !important;
  }

  .no-print {
    display: none !important;
  }

  .a4-page {
    box-shadow: none !important;
    width: 100% !important;
    min-height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .el-table {
    width: 100% !important;
    overflow: visible !important;
  }

  .el-table__body-wrapper,
  .el-table__header-wrapper,
  .el-table__fixed-body-wrapper {
    overflow: visible !important;
    height: auto !important;
  }

  .el-table__fixed,
  .el-table__fixed-right {
    display: none !important;
  }

  ::v-deep .el-descriptions__body {
    background-color: transparent !important;
  }
}
</style>
